'use client';

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { LandingResponse } from '@/modules/contents/web.interface';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import styles from './landing-page.module.less';
import TwoBlogSec from '../Common/TwoBlogSec';

export default function LandingPage({ landingData }: { landingData: LandingResponse }) {
  const { contentInfo } = landingData;
  const [open, setOpen] = useState<number | null>(0);
  return (
    <>
      {/* Section What */}
      {contentInfo['section-what'].title && (
        <section className={styles.sectionWhat}>
          <div className={styles.grid}>
            {/* Content */}
            <div className={styles.content}>
              <h2 className={styles.title}>
                {contentInfo['section-what'].title
                  .split(contentInfo['section-what'].highlight)
                  .map((part, index, array) => (
                    <span key={index}>
                      {part}
                      {index < array.length - 1 && (
                        <span className={styles.highlight}>{contentInfo['section-what'].highlight}</span>
                      )}
                    </span>
                  ))}
              </h2>
              {contentInfo['section-what'].cards.map((card) => (
                <div key={card.title} className={styles.cardWrapper}>
                  <p className={styles.cardText}>
                    {card.title.split(card.highlight).map((part, index, array) => (
                      <span key={index}>
                        {part}
                        {index < array.length - 1 && <span className={styles.textHighlight}>{card.highlight}</span>}
                      </span>
                    ))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Section Why1 */}
      {contentInfo['section-why1'].title && (
        <section className={styles.sectionWhy1}>
          <div className={styles.innerSpace}>
            {/* Section Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>{contentInfo['section-why1'].title}</h2>
              <p className={styles.description}>{contentInfo['section-why1'].desc}</p>
            </div>

            {/* Main Image */}
            {contentInfo['section-why1'].imgPath && (
              <div className={styles.imageWrapper}>
                <Image
                  width={800}
                  height={400}
                  unoptimized
                  src={contentInfo['section-why1'].imgPath}
                  alt={contentInfo['section-why1'].title}
                  className={styles.mainImage}
                />
              </div>
            )}

            {/* Cards Grid Layout */}
            {contentInfo['section-why1'].cards && contentInfo['section-why1'].cards.length >= 4 && (
              <div className={styles.cardsGrid}>
                {/* Card 0 */}
                <div className={styles.card0}>
                  <div className={styles.cardContent}>
                    <div className={styles.iconWrapper}>
                      <Image
                        width={32}
                        height={32}
                        unoptimized
                        src={contentInfo['section-why1'].cards[0].svg}
                        alt={contentInfo['section-why1'].cards[0].title}
                        className={styles.icon}
                      />
                    </div>
                    <h3 className={styles.cardTitle}>{contentInfo['section-why1'].cards[0].title}</h3>
                    <p className={styles.cardDesc}>{contentInfo['section-why1'].cards[0].desc}</p>
                  </div>
                  <img src={contentInfo['section-why1'].cards[0].imgPath || ''} className={styles.cardImage} alt="" />
                </div>

                {/* Card 1 */}
                <div className={styles.card1}>
                  <div className={styles.cardContent}>
                    <div className={styles.leftContent}>
                      <div className={styles.iconWrapper}>
                        <Image
                          width={48}
                          height={48}
                          src={contentInfo['section-why1'].cards[1].svg}
                          alt={contentInfo['section-why1'].cards[1].title}
                          className={styles.icon}
                        />
                      </div>
                      <h3 className={styles.cardTitle}>{contentInfo['section-why1'].cards[1].title}</h3>
                      <p className={styles.cardDesc}>{contentInfo['section-why1'].cards[1].desc}</p>
                    </div>
                    <img src={contentInfo['section-why1'].cards[1].imgPath || ''} className={styles.cardImage} alt="" />
                  </div>
                </div>

                {/* Card 2 */}
                <div className={styles.card2}>
                  <div className={styles.cardContent}>
                    <div className={styles.iconWrapper}>
                      <Image
                        width={48}
                        height={48}
                        src={contentInfo['section-why1'].cards[2].svg}
                        alt={contentInfo['section-why1'].cards[2].title}
                        className={styles.icon}
                      />
                    </div>
                    <h3 className={styles.cardTitle}>{contentInfo['section-why1'].cards[2].title}</h3>
                    <p className={styles.cardDesc}>{contentInfo['section-why1'].cards[2].desc}</p>
                  </div>
                </div>

                {/* Card 3 */}
                <div className={styles.card3}>
                  <div className={styles.cardContent}>
                    <div className={styles.iconWrapper}>
                      <Image
                        width={48}
                        height={48}
                        src={contentInfo['section-why1'].cards[3].svg}
                        alt={contentInfo['section-why1'].cards[3].title}
                        className={styles.icon}
                      />
                    </div>
                    <h3 className={styles.cardTitle}>{contentInfo['section-why1'].cards[3].title}</h3>
                    <p className={styles.cardDesc}>{contentInfo['section-why1'].cards[3].desc}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      {/* Section Why2 - 5 Cards Layout */}
      {contentInfo['section-why2'].title && (
        <section className={styles.sectionWhy2}>
          <div className={styles.innerSpace}>
            {/* Section Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>{contentInfo['section-why2'].title}</h2>
              <p className={styles.description}>{contentInfo['section-why2'].desc}</p>
            </div>

            {/* 5 Cards Grid Layout - 3 top, 2 bottom centered */}
            <div className={styles.cardsContainer}>
              <div className={styles.cardsWrapper}>
                {/* First Row - 3 Cards */}
                {contentInfo['section-why2'].cards?.map((card, index) => (
                  <div key={index} className={styles.why2Card}>
                    <div className={styles.cardContent}>
                      <div className={styles.iconWrapper}>
                        <Image
                          width={64}
                          height={64}
                          unoptimized
                          src={card.imgPath}
                          alt={card.title}
                          className={styles.icon}
                        />
                      </div>
                      <h3 className={styles.cardTitle}>{card.title}</h3>
                      <p className={styles.cardDesc}>{card.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      {/* Section Tools */}
      {contentInfo['section-tools'].title && (
        <section className={styles.sectionTools}>
          <div className={styles.innerSpace}>
            {/* Section Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>{contentInfo['section-tools'].title}</h2>
            </div>

            {/* Main Image */}
            {contentInfo['section-tools'].imgPath && (
              <div className={styles.imageWrapper}>
                <Image
                  width={800}
                  height={400}
                  unoptimized
                  src={contentInfo['section-tools'].imgPath}
                  alt={contentInfo['section-tools'].title}
                  className={styles.mainImage}
                />
              </div>
            )}

            {/* Cards Grid Layout */}
            {contentInfo['section-tools'].cards && contentInfo['section-tools'].cards.length > 0 && (
              <div className={styles.toolsCardsGrid}>
                {contentInfo['section-tools'].cards.map((card, index) => (
                  <div key={index} className={styles.toolCard}>
                    <div className={styles.toolCardContent}>
                      <div className={styles.iconWrapper}>
                        <Image width={54} height={54} src={card.imgPath} alt={card.title} className={styles.icon} />
                      </div>
                      <h3 className={styles.toolTitle}>{card.title}</h3>
                      {card.buttonText && (
                        <Link href={card.link} rel="noopener noreferrer" className={styles.toolButton}>
                          {card.buttonText}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      {landingData.contentInfo['section-faq'].title && (
        <div className={styles.faqSection}>
          <h2 className={styles.faqTitle}>{landingData.contentInfo['section-faq'].title}</h2>
          <div className={styles.faqDivider}>
            {landingData.faqList['content_list'].map((item, idx) => (
              <div key={item.question} className={styles.faqItem}>
                <button className={styles.faqButton} onClick={() => setOpen(open === idx ? null : idx)}>
                  <h3>{item.question}</h3>
                  <span className={styles.chevronIcon}>{open === idx ? <ChevronUp /> : <ChevronDown />}</span>
                </button>
                <div className={cn(styles.faqAnswer, open === idx ? styles.open : styles.closed)}>
                  <div dangerouslySetInnerHTML={{ __html: item.answer }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* blog */}
      {(landingData.contentInfo['section-blog']?.title || landingData.contentInfo['section-top-blog']?.title) && (
        <div className={styles.blogSection}>
          <TwoBlogSec
            blogList={landingData.blogList}
            topBlogList={landingData.topBlogList}
            blogTitle={landingData.contentInfo['section-blog']?.title}
            topBlogTitle={landingData.contentInfo['section-top-blog']?.title}
          />
        </div>
      )}
    </>
  );
}
